import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loading } from "spark-core-dx/components";
import { useCoreContext, useCoreStateContext } from "spark-core-dx/contexts";
import AIPanel from "../../../components/AIChat/AIPanel";
import { AI_PANEL_GRID_REFRESH_EVENT } from "../../../components/AIChat/aiPanelCommand";
import { ItemServiceFactory } from "../../../services/ItemServiceFactory";
import { Operations } from "../../../services/ItemServices";
import { getProduct, normalizeMarketplaceCatalog } from "../data/marketplaceCatalog";
import MarketplaceAllowancePurchaseDialog from "./MarketplaceAllowancePurchaseDialog";

const PANEL_TRANSITION_MS = 240;
const MARKETPLACE_PANEL_WIDTH = 620;

const findResource = (catalog, resourceKey) => {
  const key = String(resourceKey || "").toLowerCase();
  return (catalog?.resources || []).find((resource) =>
    String(resource.key || "").toLowerCase() === key
    || String(resource.id || resource._id || "").toLowerCase() === key);
};

const MarketplaceResourcePurchasePanel = ({
  panelMode = false,
  panelOpen = false,
  panelId = "",
  panelParams = {},
  onPanelClose
}) => {
  const coreContext = useCoreContext();
  const coreState = useCoreStateContext();
  const coreContextRef = useRef(coreContext);
  const coreStateRef = useRef(coreState);
  const [contentReady, setContentReady] = useState(!panelMode);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [selectedOfferKey, setSelectedOfferKey] = useState("");
  const [catalogReloadKey, setCatalogReloadKey] = useState(0);
  const productResourceKey = panelParams?.productResourceKey || panelParams?.ProductResourceKey || panelId;
  const resource = useMemo(() => findResource(catalog, productResourceKey), [catalog, productResourceKey]);
  const product = useMemo(() => getProduct(resource?.productKey, catalog?.products || []), [catalog?.products, resource?.productKey]);

  useEffect(() => {
    coreContextRef.current = coreContext;
    coreStateRef.current = coreState;
  }, [coreContext, coreState]);

  useEffect(() => {
    if (!panelMode) {
      setContentReady(true);
      return undefined;
    }
    if (!panelOpen) {
      setContentReady(false);
      return undefined;
    }
    const timerId = window.setTimeout(() => setContentReady(true), PANEL_TRANSITION_MS);
    return () => window.clearTimeout(timerId);
  }, [panelMode, panelOpen]);

  useEffect(() => {
    let mounted = true;

    const loadCatalog = async () => {
      if (!contentReady || !productResourceKey) return;
      setLoading(true);
      setError("");

      try {
        const activeCoreContext = coreContextRef.current;
        const activeCoreState = coreStateRef.current;
        const factory = activeCoreContext?.itemServiceFactory ?? new ItemServiceFactory();
        const service = factory.GetItemService(
          factory.ItemServiceEnum.ProductMarketplaceCatalog,
          activeCoreState,
          activeCoreContext,
          true
        );
        const response = await service.getOperation({
          operationType: Operations.ProductMarketplaceCatalogItem.LoadProductMarketplaceCatalog,
          isWarning: true,
          supressAllErrors: false
        });
        const result = response?.first?.() ?? response?.[0] ?? response;
        const catalogItem = result?.Items?.first?.() ?? result?.Items?.[0] ?? result?.Value;
        if (mounted && result?.Success && catalogItem) {
          setCatalog(normalizeMarketplaceCatalog(catalogItem));
        } else if (mounted) {
          setError("Marketplace catalog could not be loaded.");
        }
      } catch (ex) {
        if (mounted) {
          setError(ex?.message || "Marketplace catalog could not be loaded.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadCatalog();
    return () => {
      mounted = false;
    };
  }, [catalogReloadKey, contentReady, productResourceKey]);

  const dispatchWizardRefresh = (reason) => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent(AI_PANEL_GRID_REFRESH_EVENT, {
      cancelable: true,
      detail: {
        panel: "marketplaceaddresource",
        itemName: "ResourceConfigurationWizard",
        productResourceKey,
        clientResourceId: panelParams?.clientResourceId || panelParams?.ClientResourceId || "",
        resourceConfigurationId: panelParams?.resourceConfigurationId || panelParams?.ResourceConfigurationId || "",
        reason
      }
    }));
  };

  const handleAdded = () => {
    dispatchWizardRefresh("purchase");
    setPurchaseDialogOpen(false);
    setSelectedOfferKey("");
    setCatalogReloadKey((value) => value + 1);
  };

  const handleClose = () => {
    dispatchWizardRefresh("panel-close");
    onPanelClose?.();
  };

  const renderContent = () => {
    if (loading) {
      return <div className="resource-wizard__state"><Loading /></div>;
    }
    if (error) {
      return <div className="resource-wizard__alert">{error}</div>;
    }
    if (!resource) {
      return <div className="resource-wizard__state">Marketplace resource was not found.</div>;
    }

    return (
      <div className="marketplace-resource-purchase-panel">
        <header>
          <span className="resource-wizard__eyebrow">Marketplace</span>
          <h2>{resource.name || resource.key}</h2>
          <p>{resource.summary || "Buy more allowance for this resource."}</p>
        </header>
        <div className="marketplace-resource-purchase-panel__offers">
          <button
            type="button"
            className="marketplace-button marketplace-button--primary marketplace-resource-purchase-panel__choose"
            onClick={() => {
              setSelectedOfferKey("");
              setPurchaseDialogOpen(true);
            }}
          >
            Choose Package
          </button>
          {(resource.offers || []).map((offer) => (
            <button
              key={offer.key}
              type="button"
              className="marketplace-resource-purchase-panel__offer"
              onClick={() => {
                setSelectedOfferKey(offer.key);
                setPurchaseDialogOpen(true);
              }}
            >
              <span>{offer.name}</span>
              <strong>{offer.sku || offer.key}</strong>
              <small>{offer.priceSummary || "Resource package"}</small>
            </button>
          ))}
        </div>
        <MarketplaceAllowancePurchaseDialog
          open={purchaseDialogOpen}
          resource={resource}
          product={product}
          environmentKey={catalog?.environmentKey || ""}
          clientProductOffers={catalog?.clientProductOffers || []}
          initialOfferKey={selectedOfferKey}
          actionVerb="Buy"
          isSelfHostedResourceMode={catalog?.isSelfHostedResourceMode}
          onClose={() => {
            setPurchaseDialogOpen(false);
            setSelectedOfferKey("");
          }}
          onPurchased={handleAdded}
        />
      </div>
    );
  };

  return (
    <AIPanel
      open={panelMode ? panelOpen : true}
      presentation={panelMode ? "slideout" : "inline"}
      title="Buy Resource Allowance"
      className={panelMode ? "ai-panel-floating-slideout" : ""}
      width={panelMode ? MARKETPLACE_PANEL_WIDTH : undefined}
      minPanelWidth={panelMode ? MARKETPLACE_PANEL_WIDTH : undefined}
      panelStorageKey={panelMode ? null : undefined}
      contentReady={panelMode ? contentReady : true}
      chatEnabled={false}
      chatPaneToggleEnabled={false}
      singleSessionMode={true}
      onCancel={handleClose}
    >
      {renderContent()}
    </AIPanel>
  );
};

export default MarketplaceResourcePurchasePanel;
